import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download } from 'lucide-react';
import { BRAND_BLUE } from '../config/theme';

interface MobileAppBarProps {
  lang: 'en' | 'fr' | 'de' | 'it';
}

const MobileAppBar: React.FC<MobileAppBarProps> = ({ lang }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'both'>('both');

  useEffect(() => {
    // Detect platform
    const userAgent = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;
    const isAndroid = /android/i.test(userAgent);
    
    if (isIOS) {
      setPlatform('ios');
    } else if (isAndroid) {
      setPlatform('android');
    }

    // Show after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const appStoreUrl = 'https://apps.apple.com/ch/app/ttstats-swiss-table-tennis/id6744279609';
  const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.peaktwilight.ttstats';

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 shadow-lg md:hidden transition-colors duration-300"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        exit={{ y: 100 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30
        }}
      >
        <div className="px-4 py-3">
          <div className="flex items-center gap-3">
            {/* App icon - using square app icon */}
            <motion.img
              src="/app-icon.png"
              alt="TTStats"
              className="h-10 w-10 rounded-xl shadow-sm object-cover"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
            />

            {/* Text content */}
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {lang === 'fr' ? (
                  <>L'appli <span style={{ color: BRAND_BLUE }}>TT</span>Stats</>
                ) : lang === 'de' ? (
                  <>Die <span style={{ color: BRAND_BLUE }}>TT</span>Stats App</>
                ) : lang === 'it' ? (
                  <>L'appli <span style={{ color: BRAND_BLUE }}>TT</span>Stats</>
                ) : (
                  <><span style={{ color: BRAND_BLUE }}>TT</span>Stats App</>
                )}
              </p>
              <p className="text-xs text-gray-600">
                {lang === 'fr' ? 'Débloque des fonctionnalités exclusives' :
                 lang === 'de' ? 'Schalte exklusive Funktionen frei' :
                 lang === 'it' ? 'Sblocca funzionalità esclusive' :
                 'Unlock exclusive features'}
              </p>
            </div>

            {/* Download button(s) */}
            <div className="flex gap-2">
              {(platform === 'ios' || platform === 'both') && (
                <motion.a
                  href={appStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-black text-white rounded-lg text-xs font-medium hover:bg-gray-800 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="h-3 w-3" />
                  {platform === 'both' ? 'iOS' : (
                    lang === 'fr' ? 'Obtenir' :
                    lang === 'de' ? 'Holen' :
                    lang === 'it' ? 'Ottieni' :
                    'Get'
                  )}
                </motion.a>
              )}
              {(platform === 'android' || platform === 'both') && (
                <motion.a
                  href={playStoreUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-white rounded-lg text-xs font-medium transition-colors"
                  style={{
                    backgroundColor: BRAND_BLUE,
                  }}
                  whileHover={{
                    scale: 1.05,
                    filter: 'brightness(0.9)'
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Download className="h-3 w-3" />
                  {platform === 'both' ? 'Android' : (
                    lang === 'fr' ? 'Obtenir' :
                    lang === 'de' ? 'Holen' :
                    lang === 'it' ? 'Ottieni' :
                    'Get'
                  )}
                </motion.a>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MobileAppBar;